import { useTranslation } from 'react-i18next';

import { normalizeHearingInfoCallerNumber } from './helpers';

import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import {
  useCallDeafParticipantHandlers,
  useCallHearingParticipantHandlers,
} from 'features/call/call-accepted/hooks';
import { parsePhoneNumberWithRemovePlus } from 'features/call/call-base/helpers';
import { CallDaoService } from 'features/call/call-base/services';
import { RegistrationType } from 'features/call/call-deaf/enums';
import {
  callIdSelector,
  isDeafToHearingSelector,
  isHearingCallerIdBlockedSelector,
} from 'features/call/call-base/store/selectors';
import { hearingInfoReceived } from 'features/call/call-base/store/callEvents';
import { primaryHearingPhoneNumberSelector } from 'features/call/call-hearing/store';
import { handleError, showNotification } from 'features/notification/store';
import {
  deafIsLoadingSelector,
  setDeafIsLoading,
  setDeafPhoneNumber,
} from 'features/call/call-deaf/store';
import { isVoiceSessionLoadingSelector } from 'features/voice-session/store';

export const useSpawnParticipantCall = () => {
  const dispatch = useAppDispatch();
  const callId = useAppSelector(callIdSelector);
  const isDeafToHearing = useAppSelector(isDeafToHearingSelector);
  const primaryHearingPhoneNumber = useAppSelector(
    primaryHearingPhoneNumberSelector
  );
  const isHearingCallerIdBlocked = useAppSelector(
    isHearingCallerIdBlockedSelector
  );
  const isDeafLoading = useAppSelector(deafIsLoadingSelector);
  const isVoiceSessionLoading = useAppSelector(isVoiceSessionLoadingSelector);

  const { t } = useTranslation();
  const { t: addCallee } = useTranslation('translation', {
    keyPrefix: 'addCallee',
  });

  const { handleHearingSpawnNewCall } = useCallHearingParticipantHandlers();
  const { handleDeafConnect } = useCallDeafParticipantHandlers();

  const handleDeafSpawnNewCall = async (
    phoneNumber: string,
    _phoneExtension?: string
  ) => {
    dispatch(setDeafIsLoading(true));
    try {
      const formattedDeafNumber = parsePhoneNumberWithRemovePlus(phoneNumber);

      dispatch(setDeafPhoneNumber(formattedDeafNumber));
      const result = await CallDaoService.lookupHearingInfoAndUpdateCall({
        callId,
        callerNumber: normalizeHearingInfoCallerNumber(
          primaryHearingPhoneNumber
        ),
        calleeNumber: formattedDeafNumber,
        isCallerIdBlocked: isHearingCallerIdBlocked,
      });

      dispatch(hearingInfoReceived(result));

      if (result.RegistrationType === RegistrationType.NotRegistered) {
        dispatch(
          showNotification({
            severity: 'warning',
            title: t('phoneNumberNotRegistered'),
          })
        );
        throw new Error('Callee number is not registered for hearing calls');
      } else {
        await handleDeafConnect(formattedDeafNumber);
      }
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'handleDeafSpawnNewCall',
          disableUserNotification: true,
        })
      );
      dispatch(setDeafIsLoading(false));
    }
  };

  if (isDeafToHearing) {
    return {
      handleSpawnNewCall: handleHearingSpawnNewCall,
      isLoading: isVoiceSessionLoading,
      title: addCallee('addHearingCallee'),
    };
  }

  return {
    handleSpawnNewCall: handleDeafSpawnNewCall,
    isLoading: isDeafLoading,
    title: addCallee('addDeafCallee'),
  };
};
